import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Search, TrendingUp, TrendingDown, Target, Repeat, 
  ChevronDown, ChevronUp, X, Calendar as CalendarIcon,
  ExternalLink, PiggyBank
} from 'lucide-react';

/**
 * HistoriqueMensuel - Historique filtré par mois avec recherche
 * Synchronisé avec le mois affiché dans le calendrier
 * Affiche 10 opérations avec option "Voir plus"
 */
const HistoriqueMensuel = ({ 
  monthSummary, 
  currency = '€', 
  monthName, 
  year,
  onViewFullHistory 
}) => {
  const [searchText, setSearchText] = useState('');
  const [showAll, setShowAll] = useState(false);
  const INITIAL_DISPLAY_COUNT = 10;

  // Extraire toutes les opérations du mois depuis monthSummary
  const allOperations = useMemo(() => {
    if (!monthSummary?.daily_balances) return [];
    
    const ops = [];
    monthSummary.daily_balances.forEach(day => {
      if (day.operations && day.operations.length > 0) {
        day.operations.forEach(op => {
          ops.push({
            ...op,
            dayDate: day.date
          });
        });
      }
    });
    
    // Trier par date décroissante
    ops.sort((a, b) => new Date(b.date) - new Date(a.date));
    return ops;
  }, [monthSummary]);

  // Filtrer les opérations selon la recherche
  const filteredOperations = useMemo(() => {
    if (!searchText.trim()) return allOperations;
    
    const search = searchText.toLowerCase().trim();
    return allOperations.filter(op => {
      const categoryMatch = op.category?.toLowerCase().includes(search);
      const commentMatch = op.comment?.toLowerCase().includes(search);
      const amountMatch = op.amount?.toString().includes(search);
      return categoryMatch || commentMatch || amountMatch;
    });
  }, [allOperations, searchText]);

  // Opérations à afficher (avec limite)
  const displayedOperations = showAll 
    ? filteredOperations 
    : filteredOperations.slice(0, INITIAL_DISPLAY_COUNT);

  const hasMore = filteredOperations.length > INITIAL_DISPLAY_COUNT;
  const remainingCount = filteredOperations.length - INITIAL_DISPLAY_COUNT;

  // Formater la date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short',
      day: 'numeric'
    });
  };

  // Icône selon le type
  const getTypeIcon = (op) => {
    if (op.type === 'credit') return <TrendingUp className="h-4 w-4 text-emerald-600" />;
    if (op.type === 'savings') return <Target className="h-4 w-4 text-amber-600" />;
    if (op.portefeuille_depense) return <PiggyBank className="h-4 w-4 text-purple-600" />;
    return <TrendingDown className="h-4 w-4 text-rose-500" />;
  };

  // Couleur selon le type
  const getTypeBg = (op) => {
    if (op.type === 'credit') return 'bg-emerald-50 border-emerald-100';
    if (op.type === 'savings') return 'bg-amber-50 border-amber-100';
    if (op.portefeuille_depense) return 'bg-purple-50 border-purple-100';
    return 'bg-rose-50 border-rose-100';
  };

  const getAmountColor = (op) => {
    if (op.type === 'credit') return 'text-emerald-600';
    if (op.type === 'savings') return 'text-amber-600';
    if (op.portefeuille_depense) return 'text-purple-600';
    return 'text-rose-600';
  };

  if (allOperations.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-2xl border-2 border-gray-200 bg-gray-50 p-6 text-center"
        data-testid="historique-mensuel-empty"
      >
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CalendarIcon className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium">Aucune opération ce mois</p>
        <p className="text-sm text-gray-500 mt-1">
          Vos transactions apparaîtront ici
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden"
      data-testid="historique-mensuel"
    >
      {/* En-tête */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 text-base">
              Historique de {monthName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {allOperations.length} opération{allOperations.length > 1 ? 's' : ''} ce mois
            </p>
          </div>
          {onViewFullHistory && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewFullHistory}
              className="text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 gap-1"
              data-testid="view-full-history-btn"
            >
              Tout l'historique
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Barre de recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Rechercher par mot-clé, catégorie, montant..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 pr-9 h-10 bg-white border-gray-200 focus:border-teal-300 text-sm rounded-xl"
            data-testid="historique-search-input"
          />
          {searchText && (
            <button 
              onClick={() => setSearchText('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Résultat de recherche */}
        {searchText && (
          <p className="text-xs text-gray-500 mt-2">
            {filteredOperations.length} résultat{filteredOperations.length > 1 ? 's' : ''} pour "{searchText}"
          </p>
        )}
      </div>

      {/* Liste des opérations */}
      <div className="divide-y divide-gray-100">
        <AnimatePresence mode="popLayout">
          {displayedOperations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-6 text-center"
            >
              <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Aucun résultat pour "{searchText}"</p>
            </motion.div>
          ) : (
            displayedOperations.map((op, index) => (
              <motion.div
                key={op.id || `op-${index}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ delay: Math.min(index * 0.02, 0.2) }}
                className={`p-3 hover:bg-gray-50 transition-colors ${getTypeBg(op)}`}
                data-testid={`historique-op-${op.id}`}
              >
                <div className="flex items-center gap-3">
                  {/* Icône */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    op.type === 'credit' ? 'bg-emerald-100' :
                    op.type === 'savings' ? 'bg-amber-100' :
                    op.portefeuille_depense ? 'bg-purple-100' :
                    'bg-rose-100'
                  }`}>
                    {getTypeIcon(op)}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm truncate">
                        {op.category}
                      </span>
                      {op.recurring_id && (
                        <span className="flex-shrink-0">
                          <Repeat className="h-3 w-3 text-blue-500" />
                        </span>
                      )}
                      {op.is_projected && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          Prévu
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">
                        {formatDate(op.date)}
                      </span>
                      {op.comment && (
                        <span className="text-xs text-gray-400 truncate max-w-[120px]">
                          • {op.comment}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Montant */}
                  <div className={`font-bold text-sm ${getAmountColor(op)}`}>
                    {op.type === 'credit' ? '+' : '-'}{op.amount.toFixed(0)}{currency}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Bouton "Voir plus" */}
      {hasMore && !showAll && filteredOperations.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-3 border-t border-gray-100 bg-gray-50"
        >
          <Button
            variant="ghost"
            onClick={() => setShowAll(true)}
            className="w-full text-sm text-gray-600 hover:text-teal-600 hover:bg-teal-50 gap-2"
            data-testid="show-more-btn"
          >
            <ChevronDown className="h-4 w-4" />
            Voir {remainingCount} opération{remainingCount > 1 ? 's' : ''} de plus
          </Button>
        </motion.div>
      )}

      {/* Bouton "Réduire" */}
      {showAll && filteredOperations.length > INITIAL_DISPLAY_COUNT && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-3 border-t border-gray-100 bg-gray-50"
        >
          <Button
            variant="ghost"
            onClick={() => setShowAll(false)}
            className="w-full text-sm text-gray-600 hover:text-teal-600 hover:bg-teal-50 gap-2"
            data-testid="show-less-btn"
          >
            <ChevronUp className="h-4 w-4" />
            Réduire
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default HistoriqueMensuel;
